<template>
    <div class="monthly-stats-container">
        <div v-if="isCurrentMonth" class="filter-container mb-3">
            <div class="custom-btn-toggle">
                <button @click="filter = 'tillToday'" :class="{ 'active': filter === 'tillToday' }">
                    <v-icon >mdi-calendar</v-icon>Till Today
                </button>
                <button @click="filter = 'fullMonth'" :class="{ 'active': filter === 'fullMonth' }">
                    <v-icon >mdi-calendar</v-icon>Full Month
                </button>
            </div>
        </div>
        <v-layout row wrap class="gap-4">
            <v-flex v-for="(stat, index) in stats" :key="index" class="status-box-container gap-4">
                <v-card class="stat-card">
                    <v-card-text>
                        <div class="text-center">
                            <v-icon :color="stat.iconColor" size="25">{{ stat.icon }}</v-icon>
                            <div :class="['stat-value mt-1', stat.valueClass]">{{ stat.value }}</div>
                            <!-- Display the hours and minutes breakdown here -->
                            <div v-if="stat.breakdown" style="font-size: 11px; color:#555">{{ stat.breakdown }}</div>
                            <div v-else style="font-size: 11px; color:#555">--</div>
                            <div class="stat-title mt-1">{{ stat.title }}</div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-flex>
        </v-layout>
    </div>
</template>

<script>
import { subtractHolidays } from '../../utils/excludeholidayHours';

export default {
    name: "MonthlyStats",
    props: {
        logs: {
            type: Array,
            required: true,
        },
        workingDays: {
            type: Array,
            required: true,
        },
        workingHours: {
            type: [Number, String],
            required: true,
        },
        month: {
            type: String,
            required: true,
        },
        year: {
            type: String,
            required: true,
        },
        averageHours: {
            type: Number,
            default: 0,
        },
        averageMinutes: {
            type: Number,
            default: 0,
        },
        holidays: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            filter: 'fullMonth',
        };
    },
    computed: {
        isCurrentMonth() {
            const monthIndex = new Date(`${this.month} 1, ${this.year}`).getMonth();
            const year = parseInt(this.year);
            const today = new Date();
            return today.getMonth() === monthIndex && today.getFullYear() === year;
        },
        stats() {
            const monthIndex = new Date(`${this.month} 1, ${this.year}`).getMonth();
            const year = parseInt(this.year);
            const filteredLogs = this.logs.filter(log => {
                if (!log || !log.date) return false;
                const logDate = new Date(log.date);
                return logDate.getMonth() === monthIndex && logDate.getFullYear() === year;
            });

            const totalLates = this.logs.filter(
                (log) =>
                    log &&
                    log.late &&
                    (log.late.hours > 0 || log.late.minutes > 0)
            ).length;

            let totalSpentHours = 0;
            let totalSpentMinutes = 0;
            this.logs.forEach((log) => {
                if (log && log.completed) {
                    totalSpentHours += log.completed.hours || 0;
                    totalSpentMinutes += log.completed.minutes || 0;
                }
            });
            totalSpentHours += Math.floor(totalSpentMinutes / 60);
            totalSpentMinutes %= 60;

            const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
            const today = new Date();
            const maxDays = (this.filter === 'tillToday' && this.isCurrentMonth) ? today.getDate() : daysInMonth;

            let workingDaysInMonth = 0;
            for (let date = 1; date <= maxDays; date++) {
                const currentDate = new Date(year, monthIndex, date);
                if (
                    this.workingDays.includes(
                        currentDate.toLocaleDateString("en-US", { weekday: "long" })
                    )
                ) {
                    workingDaysInMonth++;
                }
            }
            workingDaysInMonth = subtractHolidays({
              holidays: this.holidays,
              currentYear: year,
              monthIndex: monthIndex,
              workingDays: this.workingDays,
              workingDaysInMonth: workingDaysInMonth,
              shift: this.$store.state.auth.user.shift || 'morning',
            });
            
            const totalRequiredHours = workingDaysInMonth * Number(this.workingHours);
            const daysWithLogs = this.logs.filter((log) => log).length;
            let averageHours = this.averageHours;
            let averageMinutes = this.averageMinutes;
            if (daysWithLogs > 0) {
                const totalHoursDecimal = totalSpentHours + totalSpentMinutes / 60;
                const averageHoursDecimal = totalHoursDecimal / daysWithLogs;
                averageHours = Math.floor(averageHoursDecimal);
                averageMinutes = Math.round((averageHoursDecimal - averageHours) * 60);
            }

            const netBalanceHours = totalSpentHours + totalSpentMinutes / 60 - totalRequiredHours;
            const netBalanceFormatted = netBalanceHours.toFixed(1);
            const isNegative = netBalanceHours < 0;

            // Convert totalRequiredHours to decimal and breakdown
            const totalRequiredDecimal = totalRequiredHours;
            const totalRequiredWholeHours = Math.floor(totalRequiredDecimal);
            const totalRequiredMinutes = Math.round((totalRequiredDecimal - totalRequiredWholeHours) * 60);

            // Convert totalSpentHours to decimal and breakdown
            const totalSpentDecimal = totalSpentHours + (totalSpentMinutes / 60);
            const totalSpentWholeHours = Math.floor(totalSpentDecimal);
            const totalSpentMinutesCalc = Math.round((totalSpentDecimal - totalSpentWholeHours) * 60);

            // Convert netBalance to decimal and breakdown
            const netBalanceDecimal = netBalanceFormatted;
            const netBalanceWholeHours = Math.floor(netBalanceDecimal);
            const netBalanceMinutes = Math.round((netBalanceDecimal - netBalanceWholeHours) * 60);

            return [
                {
                    title: "Total Lates",
                    value: `${totalLates} ${totalLates > 1 ? "days" : "day"}`,
                    icon: "mdi-clock-outline",
                    iconColor: "red",
                    valueClass: totalLates > 3 ? "error--text" : "",
                },
                {
                    title: "Average Hours Spent",
                    value: `${(averageHours + averageMinutes / 60).toFixed(1)} Hrs`,
                    breakdown: `(${averageHours} hrs, ${averageMinutes} mins)`,
                    icon: "mdi-chart-timeline-variant",
                    iconColor: "blue",
                    valueClass: "",
                },
                {
                    title: "Total Hours Required",
                    value: `${totalRequiredDecimal.toFixed(1)} Hrs`,
                    breakdown: `(${totalRequiredWholeHours} hrs, ${totalRequiredMinutes} mins)`,
                    icon: "mdi-calendar-clock",
                    iconColor: "purple",
                    valueClass: "",
                },
                {
                    title: "Total Spent Hours",
                    value: `${totalSpentDecimal.toFixed(1)} Hrs`,
                    breakdown: `(${totalSpentWholeHours} hrs, ${totalSpentMinutesCalc} mins)`,
                    icon: "mdi-timer-outline",
                    iconColor: "green",
                    valueClass: "",
                },
                {
                    title: "Net Balance",
                    value: `${netBalanceFormatted} Hrs`,
                    breakdown: `(${netBalanceWholeHours} hrs, ${netBalanceMinutes} mins)`,
                    icon: "mdi-scale-balance",
                    iconColor: isNegative ? "red" : "green",
                    valueClass: isNegative ? "error--text" : "success--text",
                },
            ];
        },
        days() {
            return this.$parent.days || [];
        }
    },
};
</script>

<style scoped>
.status-box-container {
    display: flex;
    justify-content: center;
    align-items: center;
}

.stat-card {
    border-radius: 8px;
    transition: transform 0.2s;
    min-width: 170px;
    margin: 5px auto;
   
   height: 115px;
}

.stat-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.stat-title {
    font-size: 14px;
    font-weight: 500;
    text-align: center;
}

.stat-value {
    font-size: 15px;
    font-weight: 600;
    text-align: center;
}

.v-card__text {
    padding: 8px !important;
}


.stat-description {
    font-size: 13px;
    color: #888;
    text-align: center;
}

.filter-container {
    display: flex;
    justify-content: center;
    margin-top: -5px;
    margin-bottom: 5px;
}

.custom-btn-toggle {
    display: flex;
    border-radius: 8px;
    background-color: #ece8e8;
    padding: 3px 0px;
    align-items: center;
    height: 42px;
}

.custom-btn-toggle button {
    border: none;
    gap: 3px;
    border-radius: 8px;
    padding: 5px 12px;
    height: 35px;
    font-size: 14px;
    font-weight: 500;
    text-transform: none;
    transition: all 0.2s ease;
    margin: 0 4px;
    color: #333;
    cursor: pointer;
    display: flex;
    align-items: center;
}

.custom-btn-toggle button.active {
    background-color: #ffffff !important;
    color: #333 !important;
    border: none;
    outline: none;
}

.custom-btn-toggle button:hover:not(.active) {
    background-color: #e0e0e0;
}

@media screen and (max-width: 900px) {
    .status-box-container {
        display: flex;
        justify-content: start;
        align-items: center;
    }

    .custom-btn-toggle button {
        padding: 0 10px;
        height: 28px;
        font-size: 13px;
        margin: 0 2px;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/UIComponents/MonthlyStatus.vue